<script setup lang="ts">
import type { Schemas } from "#shopware";
import { getTranslatedProperty } from "@shopware/helpers";

const { getSelectedOptions, getOptionGroups, findVariantForSelectedOptions } = useProductConfigurator();

const goToProductVariantSingleOption = async (
	group: Schemas["PropertyGroup"],
	option: Schemas["PropertyGroupOption"]
) => {
	const selectedOptions = { ...getSelectedOptions.value, [group.name]: option.id };
	goToProductVariant(selectedOptions);
};

const selectedOptions = ref(getSelectedOptions.value);
const updateRadioGroup = (configuration: Schemas["PropertyGroup"], id: string) => {
	selectedOptions.value = { ...selectedOptions.value, [configuration.name]: id };
};

const goToProductVariant = async (parentSelectedOptions: { [key: string]: string } | null = null) => {
	const variant = await findVariantForSelectedOptions(
		parentSelectedOptions ? parentSelectedOptions : selectedOptions.value
	);

	const openInfoMessage = () => {
		push.info({ message: "Deze variant kan niet geopend worden." });

		console.warn(`Cannot open product variant with ID ${variant?.id}, please check SEO rules`);
	};

	if (!variant) return openInfoMessage();

	if (!variant.seoUrls?.[0]) return navigateTo(`/detail/${variant.id}`);

	navigateTo(`/${variant.seoUrls[0].seoPathInfo}`);
	isOpen.value = false;
};

const isOpen = ref<boolean>(false);

const getRadioGroupOptions = (options: Schemas["PropertyGroupOption"][] | null) => [
	...(options?.map((option) => ({
		label: getTranslatedProperty(option, "name"),
		value: option.id
	})) ?? [])
];

const useProductConfiguratorModal = computed(() => {
	return getOptionGroups.value.length > 3;
});
</script>

<template>
	<div v-if="!useProductConfiguratorModal" class="flex flex-col gap-6">
		<template v-for="configuration in getOptionGroups" :key="configuration.id">
			<div class="flex flex-col gap-4">
				<span class="font-medium">{{ getTranslatedProperty(configuration, "name") }}</span>
				<div class="flex flex-wrap gap-2">
					<KippieButton
						:color="Object.values(getSelectedOptions).includes(option.id) ? 'black' : 'white'"
						v-for="option in configuration.options"
						:key="option.id"
						size="small"
						@click="goToProductVariantSingleOption(configuration, option)"
					>
						{{ getTranslatedProperty(option, "name") }}
					</KippieButton>
				</div>
			</div>
			<Divider />
		</template>
	</div>
	<div v-else class="flex flex-col gap-6">
		<KippieButton color="yellow" block justify="space-between" @click="isOpen = true">
			Stel jouw product samen
			<template #right>
				<SvgIcon name="puzzle" />
			</template>
		</KippieButton>
		<Teleport to="body">
			<KippieSidebar v-model="isOpen" placement="right" color="white">
				<template #top>
					<Typography variant="h3">Product samenstelling</Typography>
				</template>
				<template v-for="configuration in getOptionGroups" :key="configuration.id">
					<div class="flex flex-col gap-6 py-2">
						<Typography variant="h6" class="font-medium">
							{{ getTranslatedProperty(configuration, "name") }}
						</Typography>
						<div class="flex flex-wrap gap-2">
							<KippieRadioGroup
								v-if="configuration.options"
								:values="getRadioGroupOptions(configuration.options)"
								:initial-value="getSelectedOptions[configuration.name]"
								@update="updateRadioGroup(configuration, $event)"
							/>
						</div>
						<Divider />
					</div>
				</template>
				<KippieButton size="small" class="my-2" @click="goToProductVariant(null)">
					Bekijk samengesteld product
				</KippieButton>
			</KippieSidebar>
		</Teleport>
	</div>
</template>
